import { Box, Text } from '@air/zephyr';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import widont from 'widont';

import { ContentTypes } from '~/lib/contentful/constants/contentTypes';
import { ButtonData, TextSectionData } from '~/lib/contentful/custom-types';
import { Container } from '~/old-marketing/components/Container';
import { Section } from '~/old-marketing/components/Section';
import { buttonLinkRenderer } from '~/old-marketing/utils/buttonLinkRenderer';
import { linkRenderer } from '~/old-marketing/utils/linkRenderer';

export interface TextSectionProps extends TextSectionData {
  index?: number;
  isSubsection?: boolean;
}

export const TextSection = ({
  __typename,
  actions,
  alignment,
  backgroundColor,
  backgroundImage,
  content,
  contentTitle,
  id,
  index,
  isSubsection,
  maxWidth,
  title,
}: TextSectionProps) => {
  return (
    <Section
      analytics={{
        contentId: id,
        contentTitle,
        contentType: __typename,
      }}
      id={`${__typename}-${id}-${index}`}
      index={index}
      tx={{
        backgroundColor: backgroundColor ?? null,
        backgroundImage: backgroundImage ? `url(${backgroundImage?.file.url})` : null,
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100%',
        textAlign: alignment ?? null,
        py: isSubsection ? [32, 32, 32] : [64, 64, 80],
      }}
    >
      <Container>
        <Box tx={{ maxWidth, mx: 'auto' }}>
          {title && (
            <Text
              as={isSubsection ? 'h3' : 'h2'}
              tx={{ color: 'jay700', fontWeight: 'semibold' }}
              variant={['text-heading-32', 'text-heading-32', 'text-heading-40']}
            >
              {widont(title)}
            </Text>
          )}

          {content && (
            <Text tx={{ color: 'pigeon700', mt: 32 }} variant={['text-ui-16', 'text-ui-16', 'text-ui-18']}>
              {documentToReactComponents(content)}
            </Text>
          )}

          {actions && (
            <Box tx={{ mt: 32 }}>
              {actions.map((action) => {
                const isLink = action.__typename === ContentTypes.components.link;

                if (isLink) {
                  return linkRenderer({
                    analytics: {
                      name: action.text,
                      section: {
                        contentId: id,
                        contentTitle,
                        contentType: __typename,
                      },
                    },
                    children: action.text,
                    href: action.url,
                    tx: { mx: 8 },
                    type: action.type,
                  });
                }

                return buttonLinkRenderer({
                  analytics: {
                    name: action.text,
                    section: {
                      contentId: id,
                      contentTitle,
                      contentType: __typename,
                    },
                  },
                  children: action.text,
                  href: action.url,
                  size: (action as ButtonData).size,
                  tx: { mr: 8 },
                  type: action.type,
                  /** @ts-ignore temporary until we convert all marketing views to use the newest version of the parser */
                  variant: action.variant,
                });
              })}
            </Box>
          )}
        </Box>
      </Container>
    </Section>
  );
};
